/**
 * monthlyAttendanceApi.js
 * -----------------------
 * API client for Education > Attendance > Monthly Attendance submodule.
 * Connects directly to FastAPI backend with zero mock data.
 */
import api from '../../../../api/client'

export async function fetchMonthlyAttendance(monthYearString, filters = {}, sortBy = '1') {
  return fetchMonthlyAttendanceMatrix(monthYearString, filters, sortBy)
}

export async function fetchMonthlyAttendanceMatrix(monthYearString, filters = {}, sortBy = 'rollNo', sortOrder = 'asc') {
  // Parse monthYearString e.g. "2026-10"
  let year = new Date().getFullYear()
  let month = new Date().getMonth() + 1

  if (monthYearString && monthYearString.includes('-')) {
    const [yStr, mStr] = monthYearString.split('-')
    year = parseInt(yStr, 10) || year
    month = parseInt(mStr, 10) || month
  }

  const params = {
    year,
    month,
  }

  if (filters.academicYear && filters.academicYear !== 'All') {
    params.academic_year = filters.academicYear
  }
  if (filters.branch && filters.branch !== 'All' && !isNaN(filters.branch)) {
    params.branch_id = parseInt(filters.branch, 10)
  }
  if (filters.course && filters.course !== 'All' && !isNaN(filters.course)) {
    params.course_id = parseInt(filters.course, 10)
  }
  if (filters.batch && filters.batch !== 'All' && !isNaN(filters.batch)) {
    params.batch_id = parseInt(filters.batch, 10)
  }
  if (filters.subject && filters.subject !== 'All' && !isNaN(filters.subject)) {
    params.subject_id = parseInt(filters.subject, 10)
  }
  if (filters.school && filters.school !== 'All' && !isNaN(filters.school)) {
    params.school_id = parseInt(filters.school, 10)
  }

  params.order_by = sortBy === 'rollNo' ? 2 : 1

  const { data } = await api.get('/attendance/monthly', { params })

  const totalDays = data?.total_days || 31
  const daysMeta = []
  let workingDaysCount = 0

  for (let d = 1; d <= totalDays; d++) {
    const dObj = new Date(year, month - 1, d)
    const isSunday = dObj.getDay() === 0
    const dayName = dObj.toLocaleDateString('en-US', { weekday: 'narrow' })
    if (!isSunday) workingDaysCount++
    daysMeta.push({
      day: d,
      dayName,
      isSunday,
    })
  }

  let totalPresentAcrossClass = 0
  let totalOpportunities = 0
  let defaultersCount = 0

  const rawStudents = data?.students || []
  const studentRows = rawStudents.map((s) => {
    const dayAttendance = {}
    let presentCount = s.present || 0
    let absentCount = s.absent || 0
    let holidayCount = 0

    daysMeta.forEach((dMeta) => {
      const dayNum = dMeta.day
      const statusFromBackend = s.days ? s.days[dayNum] : ' '
      if (dMeta.isSunday) {
        dayAttendance[dayNum] = 'H'
        holidayCount++
      } else {
        dayAttendance[dayNum] = statusFromBackend && statusFromBackend.trim() ? statusFromBackend.trim() : ' '
      }
    })

    const workingDays = presentCount + absentCount
    const pct = workingDays > 0 ? Math.round((presentCount / workingDays) * 100) : (s.percentage || 0)

    totalPresentAcrossClass += presentCount
    totalOpportunities += workingDays

    if (pct < 75) {
      defaultersCount++
    }

    return {
      id: s.id,
      studentId: s.id_card_no ? String(s.id_card_no) : `STU-${String(s.id).padStart(4, '0')}`,
      fullName: s.full_name,
      rollNo: s.roll_no ? String(s.roll_no) : '',
      courseName: s.course_name,
      batchNames: s.batch_names,
      branchName: s.branch_name,
      dayAttendance,
      presentCount,
      absentCount,
      holidayCount,
      percentage: pct,
    }
  })

  const avgClassPct = totalOpportunities > 0 ? Math.round((totalPresentAcrossClass / totalOpportunities) * 100) : 0

  return {
    daysMeta,
    students: studentRows,
    totalStudents: studentRows.length,
    metrics: {
      totalStudents: studentRows.length,
      averagePercentage: avgClassPct,
      workingDays: workingDaysCount,
      totalDays,
      defaultersCount,
    },
  }
}
